import React, { useEffect, useState } from "react"
import clsx from "clsx"
import MyModal from "@common-utils/MyModal"
import { ExtractedCandidate } from "../../CandidatImport"

interface Props {
     preview: { url: string; data: ExtractedCandidate; index: number } | null
     duplicateEmails: string[]
     navInfo: { prevIdx: number; nextIdx: number; current: number; total: number }
     onClose: () => void
     onNavigate: (index: number) => void
     onUpdate: (field: keyof ExtractedCandidate, val: any) => void
}

const Step2VerificationModal = ({ preview, duplicateEmails, navInfo, onClose, onNavigate, onUpdate }: Props) => {
     // State to switch between preview and form on mobile
     const [mobileView, setMobileView] = useState<"preview" | "form">("preview")

     useEffect(() => {
          const handleKeyDown = (e: KeyboardEvent) => {
               if (e.key === "ArrowLeft" && navInfo.prevIdx !== -1) onNavigate(navInfo.prevIdx)
               if (e.key === "ArrowRight" && navInfo.nextIdx !== -1) onNavigate(navInfo.nextIdx)
          }
          window.addEventListener("keydown", handleKeyDown)
          return () => window.removeEventListener("keydown", handleKeyDown)
     }, [navInfo, onNavigate])

     if (!preview) return null
     const isDuplicate = duplicateEmails.includes(preview.data.email)

     return (
          <MyModal
               show={!!preview}
               onCloseCallback={onClose}
               fullscreen={true}
               noPadding={true}
               title={
                    <div className="d-flex align-items-center justify-content-between w-100">
                         <div className="d-flex align-items-center gap-6">
                              <div className="d-flex align-items-center">
                                   <button className="btn btn-icon btn-xs btn-active-color-primary" disabled={navInfo.prevIdx === -1} onClick={() => onNavigate(navInfo.prevIdx)}>
                                        <i className="fas fa-chevron-left fs-9"></i>
                                   </button>
                                   <span className="fw-boldest fs-8 px-4 text-primary">
                                        {navInfo.current} / {navInfo.total}
                                   </span>
                                   <button className="btn btn-icon btn-xs btn-active-color-primary" disabled={navInfo.nextIdx === -1} onClick={() => onNavigate(navInfo.nextIdx)}>
                                        <i className="fas fa-chevron-right fs-9"></i>
                                   </button>
                              </div>
                              <div className="d-flex flex-column">
                                   <span className="text-gray-400 fw-bold fs-9 text-uppercase lh-1 mb-1">Candidat</span>
                                   <span className={clsx("fw-boldest fs-6 lh-1", isDuplicate ? "text-danger" : "text-gray-900")}>
                                        {preview.data.firstname} {preview.data.lastname}
                                   </span>
                              </div>
                         </div>

                         {/* Mobile view toggle switches in title */}
                         <div className="d-lg-none ms-15">
                              <div className="btn-group bg-body shadow-sm">
                                   <button
                                        className={clsx("btn btn-icon btn-sm", mobileView === "preview" ? "btn-primary" : "btn-active-light-primary")}
                                        onClick={() => setMobileView("preview")}
                                   >
                                        <i className="fas fa-eye fs-6"></i>
                                   </button>
                                   <button
                                        className={clsx("btn btn-icon btn-sm", mobileView === "form" ? "btn-primary" : "btn-active-light-primary")}
                                        onClick={() => setMobileView("form")}
                                   >
                                        <i className="fas fa-edit fs-6"></i>
                                   </button>
                              </div>
                         </div>
                    </div>
               }
          >
               <div className="d-flex flex-column flex-lg-row h-100 bg-gray-100 overflow-hidden">
                    {/* Left Pane: Curriculum Preview (Full page on mobile if selected) */}
                    <div className={clsx("flex-grow-1 bg-gray-300 position-relative h-100", mobileView !== "preview" && "d-none d-lg-block")} style={{ minWidth: "60%" }}>
                         <iframe src={preview.url} width="100%" height="100%" title="CV Preview" style={{ border: "none" }} />
                    </div>

                    {/* Right Pane: Form (Full page on mobile if selected) */}
                    <div
                         className={clsx("bg-body h-100 d-flex flex-column w-lg-450px border-start shadow-sm", mobileView !== "form" && "d-none d-lg-block")}
                         style={{ width: "100%", minWidth: "350px" }}
                    >
                         <div className="flex-grow-1 overflow-auto p-10">
                              <div className="mb-10 d-flex align-items-center">
                                   <div className="symbol symbol-40px me-4">
                                        <div className="symbol-label bg-light-primary">
                                             <i className="fas fa-check-double text-primary fs-4"></i>
                                        </div>
                                   </div>
                                   <div>
                                        <h4 className="fw-bolder text-gray-900 mb-0">Vérification des données</h4>
                                        <p className="text-muted fs-7 mb-0">Corrigez les informations si nécessaire.</p>
                                   </div>
                              </div>

                              <div className="d-flex flex-column gap-6">
                                   <div className="fv-row">
                                        <label className="form-label fw-bold fs-7 text-uppercase">Civilité</label>
                                        <select className="form-select form-select-solid" value={preview.data.civility} onChange={e => onUpdate("civility", e.target.value)}>
                                             <option value="CIVILITE_MR">Monsieur</option>
                                             <option value="CIVILITE_MME">Madame</option>
                                        </select>
                                   </div>
                                   <div className="row g-5">
                                        <div className="col-6">
                                             <label className="form-label fw-bold fs-7 text-uppercase">Prénom</label>
                                             <input
                                                  className="form-control form-control-solid"
                                                  value={preview.data.firstname}
                                                  onChange={e => onUpdate("firstname", e.target.value)}
                                             />
                                        </div>
                                        <div className="col-6">
                                             <label className="form-label fw-bold fs-7 text-uppercase">Nom</label>
                                             <input
                                                  className="form-control form-control-solid"
                                                  value={preview.data.lastname}
                                                  onChange={e => onUpdate("lastname", e.target.value)}
                                             />
                                        </div>
                                   </div>
                                   <div className={clsx("p-5 rounded border border-dashed", isDuplicate ? "bg-light-danger border-danger" : "bg-light-primary border-primary")}>
                                        <label className={clsx("form-label fw-bold fs-7 text-uppercase mb-4", isDuplicate ? "text-danger" : "text-primary")}>Coordonnées</label>
                                        <div className="mb-4">
                                             <label className="fs-8 fw-bold text-gray-600 mb-1 text-uppercase">Téléphone</label>
                                             <input
                                                  className="form-control form-control-solid"
                                                  value={preview.data.phoneNumber}
                                                  onChange={e => onUpdate("phoneNumber", e.target.value)}
                                             />
                                        </div>
                                        <div>
                                             <label className="fs-8 fw-bold text-gray-600 mb-1 text-uppercase">Email</label>
                                             <input
                                                  className={clsx("form-control form-control-solid border", isDuplicate && "border-danger text-danger fw-bolder")}
                                                  value={preview.data.email}
                                                  onChange={e => onUpdate("email", e.target.value)}
                                             />
                                        </div>
                                   </div>
                              </div>
                         </div>
                         <div className="p-8 bg-light border-top">
                              <button className="btn btn-primary w-100 shadow-sm" onClick={onClose}>
                                   <i className="fas fa-check me-2"></i> Terminer la vérification
                              </button>
                         </div>
                    </div>
               </div>
          </MyModal>
     )
}

export default Step2VerificationModal
